import { formatRefDate } from '../../utils/formatRefDate.js';

// Compare API clock fields without applying the browser's timezone.
export function readingKey(value) {
    if (formatRefDate(value) === '—') return '';
    const raw = String(value).trim().replace('T', ' ');
    const fraction = raw.match(/\.(\d+)/)?.[1] || '';
    return `${raw.slice(0, 19)}.${fraction.padEnd(9, '0').slice(0, 9)}`;
}

export function vesselStatus(ship) {
    if (ship.currentState === 'loading' || ship.currentState === 'error') return null;
    const last = ship.current?.[ship.current.length - 1];
    if (last?.Status == null) return null;
    return last.Status === '0' ? false : Boolean(last.Status);
}

export function vesselStatusLabel(ship) {
    if (ship.currentState === 'loading') return 'Loading status...';
    if (ship.currentState === 'error') return 'Unavailable';
    if (ship.currentState === 'empty') return 'No data';
    const status = vesselStatus(ship);
    return status === null ? 'Unknown' : status ? 'Online' : 'Offline';
}

export function vesselKey(ship) {
    return JSON.stringify([ship?.KodeCabang || ship?.AccountId || '', ship?.ShipCode || ship?.ObjectCode || '']);
}

export function setupMonitoringVessels(setup = []) {
    const vessels = new Map();
    for (const row of setup) {
        const AccountId = String(row.AccountId || '').trim();
        const ObjectCode = String(row.ObjectCode || '').trim();
        if (!AccountId || !ObjectCode || AccountId.toUpperCase() === 'SINERGI') continue;
        // Setup has no ship-name column; its object code is the vessel identity.
        const vessel = {
            AccountId, ObjectCode,
            KodeCabang: AccountId, NamaCabang: AccountId,
            ShipCode: ObjectCode, ShipName: ObjectCode,
            current: [], currentState: 'loading',
            email: null, emailState: 'loading',
        };
        vessels.set(vesselKey(vessel), vessel);
    }
    return [...vessels.values()];
}

export function positionReports(ships = []) {
    const reports = new Map();
    for (const ship of ships) {
        const parts = typeof ship.email?.coordinates === 'string' ? ship.email.coordinates.split(',') : [];
        if (parts.length !== 2 || parts.some(value => !value.trim())) continue;
        const [lat, lng] = parts.map(Number);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
        const reportDate = ship.email.date && ship.email.time ? `${ship.email.date} ${ship.email.time}` : '';
        const key = vesselKey(ship);
        const report = { ...ship, key, lat, lng, reportDate };
        if (!reports.has(key) || readingKey(reportDate) > readingKey(reports.get(key).reportDate)) reports.set(key, report);
    }
    return [...reports.values()].sort((a, b) => readingKey(b.reportDate).localeCompare(readingKey(a.reportDate)));
}

export function monitoringSummary(ships = [], reports = []) {
    let latest = '';
    for (const ship of ships) for (const sensor of ship.current || []) {
        if (readingKey(sensor.RefDate) > readingKey(latest)) latest = sensor.RefDate;
    }
    const online = ships.filter(ship => vesselStatus(ship) === true).length;
    const offline = ships.filter(ship => vesselStatus(ship) === false).length;
    return {
        total: ships.filter(ship => ship.current?.length > 0).length,
        online, offline, knownStatuses: online + offline, locations: reports.length, latest,
    };
}

function latestReading(current = []) {
    return current.reduce((latest, sensor) => readingKey(sensor.RefDate) > readingKey(latest) ? sensor.RefDate : latest, '');
}

export function monitoringVessels(ships = [], reports = []) {
    const reportsByKey = new Map(reports.map(report => [report.key, report]));
    return ships.map((ship, sourceIndex) => ({
        ...ship,
        key: vesselKey(ship),
        sourceIndex,
        status: vesselStatus(ship),
        lastReading: latestReading(ship.current),
        report: reportsByKey.get(vesselKey(ship)) || null,
    }));
}

export function filterMonitoringVessels(vessels = [], search = '', status = 'all') {
    const query = search.trim().toLowerCase();
    return vessels.filter(vessel => {
        const matchesText = !query || [vessel.ShipName, vessel.ShipCode, vessel.KodeCabang]
            .some(value => String(value || '').toLowerCase().includes(query));
        const matchesStatus = status === 'all' || (status === 'online' ? vessel.status === true : vessel.status === false);
        return matchesText && matchesStatus;
    });
}
